import { Injectable, inject } from '@angular/core';
import { supabase } from '../../core/supabase/supabase.client';
import {
  ImageAssetUploadService,
  type ImageAssetKind,
} from './image-asset-upload.service';

type FileCandidate = {
  id: number;
  file_name?: string | null;
  bucket: string;
  path: string;
  entity_type?: string | null;
  purpose?: string | null;
  metadata?: Record<string, any> | null;
};

export type ImageBackfillReport = {
  scanned: number;
  migrated: number;
  skipped: number;
  failed: Array<{ fileId: number; message: string }>;
};

const kindForFile = (file: FileCandidate): ImageAssetKind => {
  if (/tab/i.test(file.entity_type || '') || file.purpose === 'icon') {
    return file.entity_type === 'category' ? 'category' : 'home_tab_icon';
  }
  if (file.entity_type === 'product') return 'product';
  if (/banner/i.test(file.purpose || '')) return 'banner';
  return /home|collection/i.test(file.entity_type || '')
    ? 'banner'
    : 'collection_tile';
};

const collectConfigFileIds = (value: unknown, ids: Set<number>) => {
  if (!value || typeof value !== 'object') return;
  if (Array.isArray(value)) {
    value.forEach(item => collectConfigFileIds(item, ids));
    return;
  }
  Object.entries(value).forEach(([key, child]) => {
    if (/_file_id$/i.test(key) && Number.isFinite(Number(child))) {
      ids.add(Number(child));
    } else {
      collectConfigFileIds(child, ids);
    }
  });
};

const attachConfigVariants = (
  value: unknown,
  metadataByFileId: Map<number, Record<string, any>>,
): boolean => {
  if (!value || typeof value !== 'object') return false;
  if (Array.isArray(value)) {
    let changed = false;
    value.forEach(item => {
      if (attachConfigVariants(item, metadataByFileId)) changed = true;
    });
    return changed;
  }
  let changed = false;
  const record = value as Record<string, any>;
  for (const [key, child] of Object.entries(record)) {
    if (/_file_id$/i.test(key) && Number.isFinite(Number(child))) {
      const metadata = metadataByFileId.get(Number(child));
      if (!metadata?.['variants']) continue;
      const base = key.replace(/_file_id$/i, '');
      record[`${base}_variants`] = metadata['variants'];
      record[`${base}_version`] = metadata['version'];
      changed = true;
    } else if (attachConfigVariants(child, metadataByFileId)) {
      changed = true;
    }
  }
  return changed;
};

/** Runs under the current authenticated Admin session; no service key is used. */
@Injectable({ providedIn: 'root' })
export class ImageVariantBackfillService {
  private readonly assets = inject(ImageAssetUploadService);

  async runActiveHomeBackfill(): Promise<ImageBackfillReport> {
    const fileIds = new Set<number>();
    const [{ data: tabs, error: tabsError }, { data: sections, error: sectionsError }] =
      await Promise.all([
        supabase
          .from('home_tabs')
          .select('id, icon_file_id, meta')
          .eq('is_active', true),
        supabase
          .from('home_sections')
          .select('id, config')
          .eq('is_active', true),
      ]);
    if (tabsError) throw tabsError;
    if (sectionsError) throw sectionsError;
    (tabs ?? []).forEach((tab: any) => {
      if (tab.icon_file_id) fileIds.add(Number(tab.icon_file_id));
      collectConfigFileIds(tab.meta, fileIds);
    });
    (sections ?? []).forEach((section: any) =>
      collectConfigFileIds(section.config, fileIds),
    );

    const [{ data: categories, error: categoriesError }, { data: products, error: productsError }] =
      await Promise.all([
        supabase.from('categories').select('icon_file_id').eq('is_active', true),
        supabase
          .from('products')
          .select('images:product_images(file_id, is_primary)')
          .eq('is_active', true),
      ]);
    if (categoriesError) throw categoriesError;
    if (productsError) throw productsError;
    (categories ?? []).forEach((category: any) => {
      if (category.icon_file_id) fileIds.add(Number(category.icon_file_id));
    });
    (products ?? []).forEach((product: any) =>
      (product.images ?? [])
        .filter((image: any) => image.is_primary)
        .forEach((image: any) => fileIds.add(Number(image.file_id))),
    );

    if (!fileIds.size) return { scanned: 0, migrated: 0, skipped: 0, failed: [] };
    const { data: files, error: filesError } = await supabase
      .from('files')
      .select('id, file_name, bucket, path, entity_type, purpose, metadata')
      .in('id', [...fileIds]);
    if (filesError) throw filesError;

    const report: ImageBackfillReport = {
      scanned: files?.length ?? 0,
      migrated: 0,
      skipped: 0,
      failed: [],
    };
    const metadataByFileId = new Map<number, Record<string, any>>();
    for (const file of (files ?? []) as FileCandidate[]) {
      try {
        const result = await this.assets.backfillExistingFile({
          id: file.id,
          fileName: file.file_name,
          bucket: file.bucket,
          path: file.path,
          metadata: file.metadata,
          kind: kindForFile(file),
        });
        if (result.changed) report.migrated += 1;
        else report.skipped += 1;
        if (result.metadata) metadataByFileId.set(file.id, result.metadata);
      } catch (error) {
        report.failed.push({
          fileId: file.id,
          message: error instanceof Error ? error.message : String(error),
        });
      }
    }

    for (const tab of tabs ?? []) {
      const meta = { ...((tab as any).meta || {}) };
      const iconMetadata = metadataByFileId.get(Number((tab as any).icon_file_id));
      let changed = attachConfigVariants(meta, metadataByFileId);
      if (iconMetadata?.['variants']) {
        meta.icon_variants = iconMetadata['variants'];
        meta.icon_version = iconMetadata['version'];
        changed = true;
      }
      if (changed) {
        const { error } = await supabase
          .from('home_tabs')
          .update({ meta })
          .eq('id', (tab as any).id);
        if (error) throw error;
      }
    }
    for (const section of sections ?? []) {
      const config = structuredClone((section as any).config || {});
      if (attachConfigVariants(config, metadataByFileId)) {
        const { error } = await supabase
          .from('home_sections')
          .update({ config })
          .eq('id', (section as any).id);
        if (error) throw error;
      }
    }
    return report;
  }
}
